import { useQuery } from '@tanstack/react-query';
import { getProcessesCounts } from '@api/processes';

/**
 * Per-status totals shown next to each segmented-control option (e.g.
 * "Published 12"). Each value is `undefined` while the request is still in
 * flight (the consuming `SegmentedControl` hides the badge in that case
 * rather than flashing a spurious "0") and resolves to the server-side
 * count once the request settles.
 *
 * Counts respect the active search filter — switching tabs reflects what the
 * user would actually see in the list (more useful than absolute totals).
 */
export interface ProcessesCounts {
  all: number | undefined;
  published: number | undefined;
  draft: number | undefined;
}

const STALE_MS = 30_000;

const countsQueryKey = (search: string) =>
  ['processes', 'counts', search] as const;

/**
 * Single round-trip to `GET /api/v1/processes/counts` returning
 * {all, draft, published}. Replaces the previous three `?size=1` list
 * calls — the segmented-control badges now share a query key so React
 * Query dedupes across tab switches and we keep the network footprint
 * to one request per search-term change.
 */
export function useProcessesCounts(search: string): ProcessesCounts {
  const query = useQuery({
    queryKey: countsQueryKey(search),
    queryFn: () => getProcessesCounts(search.trim() ? search.trim() : undefined),
    staleTime: STALE_MS,
    placeholderData: (prev) => prev,
  });

  return {
    all: query.data?.all,
    published: query.data?.published,
    draft: query.data?.draft,
  };
}
